import { CampaignOutput } from '@/api/gql/graphql';
import {
  useGetCampaign,
  useGetCampaignDetail,
  useGetCampaignsStats,
} from '@/api/hooks/b2bFinance/useCampaign';
import iconStatCampaign from '@/assets/icons/stat-campaign.svg';
import { ColumnDefinition } from '@/components/Ui/DataTable/DataTable.type';
import { useCampaignStore } from '@/store/CampaignStore/CampaignStore';
import { useCallback, useState } from 'react';
import { useFormatDate } from '../../../hooks/useFormatDate';

export enum Status {
  PENDING = 'PENDING',
  RUNNING = 'RUNNING',
  COMPLETED = 'COMPLETED',
}

export enum CampaignType {
  WELCOME_POPUP = 'WELCOME_POPUP',
  MAIN_BANNER = 'MAIN_BANNER',
  AUDIO_PRE_LECON = 'AUDIO_PRE_LECON',
  TEXT_LESSON_POPUP = 'TEXT_LESSON_POPUP',
  TEXT_LESSON_BANNER = 'TEXT_LESSON_BANNER',
}

export const statusConfig: Record<Status, { label: string; color: string }> = {
  [Status.PENDING]: { label: 'En attente', color: 'text-pending' },
  [Status.RUNNING]: { label: 'En cours', color: 'text-running' },
  [Status.COMPLETED]: { label: 'Terminée', color: 'text-completed' },
};

export const campaignTypeConfig: Record<CampaignType, { label: string }> = {
  [CampaignType.WELCOME_POPUP]: { label: 'Popup de Bienvenue' },
  [CampaignType.MAIN_BANNER]: { label: 'Bannière Principale' },
  [CampaignType.AUDIO_PRE_LECON]: { label: 'Publicité Audio Pré-leçon' },
  [CampaignType.TEXT_LESSON_POPUP]: { label: 'Popup Pré-leçon Texte' },
  [CampaignType.TEXT_LESSON_BANNER]: { label: 'Bannière de Leçon Texte' },
};

export const columns: ColumnDefinition[] = [
  { field: 'priority', header: 'Priorité' },
  { field: 'companyName', header: 'Entreprise' },
  { field: 'campaignName', header: 'Nom campagne' },
  { field: 'type', header: 'Type' },
  { field: 'startDate', header: 'Date début' },
  { field: 'duration', header: 'Durée' },
  { field: 'endDate', header: 'Date fin' },
  { field: 'amount', header: 'Montant' },
  { field: 'status', header: 'Statut' },
  { field: 'sheet', header: '', sortable: false },
];

export const getStatusDisplay = (status: string) => {
  const statusKey = status as Status;
  const config = statusConfig[statusKey];
  return config ? (
    <span className={config.color}>{config.label}</span>
  ) : (
    <span>{status}</span>
  );
};

export const getCampaignType = (type: CampaignType) => {
  const config = campaignTypeConfig[type];
  return config ? <span>{config.label}</span> : <span>{type}</span>;
};

export const useCampaign = () => {
  const { formatDate, getDurationInDays } = useFormatDate();
  const {
    openCampaignModal,
    setCampainListLimit,
    openCampaignDetailModal,
    campainListlimit,
    campaignListPage,
    setCampainListPage,
  } = useCampaignStore();

  const {
    data: campaignsData,
    isLoading,
    error,
  } = useGetCampaign({ limit: campainListlimit, page: campaignListPage });

  const [selectedCampaignId, setSelectedCampaignId] = useState<string>('');

  const {
    data: campaignDetailData,
    isLoading: isCampaignDetailLoading,
    error: campaignDetailError,
  } = useGetCampaignDetail(selectedCampaignId);

  const { data: campaignsStats } = useGetCampaignsStats();

  const handleCampaignDetail = useCallback(
    (campaignId: string) => {
      setSelectedCampaignId(campaignId);
      openCampaignDetailModal();
    },
    [openCampaignDetailModal],
  );

  const formattedData = campaignsData?.data?.map(
    (campaign: CampaignOutput) => ({
      _id: campaign._id,
      priority: campaign.campaignPriority,
      companyName: campaign.company.name,
      campaignName: campaign.campaignName,
      type: getCampaignType(campaign.campaignType as CampaignType),
      startDate: formatDate(campaign.campaignStartDate),
      endDate: formatDate(campaign.campaignEndDate),
      duration: (
        <span>
          {getDurationInDays(
            campaign.campaignStartDate,
            campaign.campaignEndDate,
          )}
          j
        </span>
      ),
      amount: campaign.campaignAmount,
      status: getStatusDisplay(campaign.campaignStatus.toLocaleUpperCase()),
      sheet: (
        <span
          role="img"
          className="block cursor-pointer"
          aria-label="Icone statistiques"
        >
          <img
            src={iconStatCampaign}
            alt="Icone statistiques"
            onClick={() => handleCampaignDetail(campaign._id)}
          />
        </span>
      ),
    }),
  );

  return {
    columns,
    campaignsData,
    formattedData,
    isLoading,
    error,
    campaignDetailData,
    isCampaignDetailLoading,
    campaignDetailError,
    setCampainListPage,
    campaignListPage,
    openCampaignModal,
    campainListlimit,
    setCampainListLimit,
    campaignsStats,
  };
};
